import { Injectable } from '@angular/core';

import { AccessorySaveData } from '../classes/accessory-save-data';
import { Position } from '../classes/position';
import { Rotation } from '../classes/rotation';
import { StageService } from './stage.service';
import { AwningSaveDataService } from './awning/awning-save-data.service';
import { ProjectManagerService } from './project-manager.service';
import { switchMap, take, catchError, concatMap, map } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/throw';
import { SnapShotService } from './snap-shot.service';
import { DeckHeightService } from './deck-height.service';
import { SetMaterialsService } from './set-materials.service';
import { DataForDisplayingMenuItemsService } from './data-for-displaying-menu-items.service';
import { ToasterService } from 'angular2-toaster';
import { RendererService } from './renderer.service';
import { RoomStateManagerService } from './room-state-manager.service';
import { AppSettingsService } from './app-settings.service';
import { ScreenshotPreprocessService } from './screenshot-preprocess.service';
import { AuthService } from './auth.service';
import { Project } from '../classes/project';
import { ProToolsUiManagerService } from './pro-tools-ui-manager.service';
import { DrawingsHolderService } from './proTools/drawings-holder.service';
import { Subject } from 'rxjs';

declare let THREE: any;

@Injectable()
export class ProjectSaveService {
  _toDeleteProjectImg: string = null;
  proToolsActive: boolean;
  projectSaved$ = new Subject<void>();
  constructor(
    private stageService: StageService,
    private awningSaveDataService: AwningSaveDataService,
    private projectManagerService: ProjectManagerService,
    private deckHeightService: DeckHeightService,
    private setMaterialsService: SetMaterialsService,
    private dataForDisplayingMenuItemsService: DataForDisplayingMenuItemsService,
    private toasterService: ToasterService,
    private rendererService: RendererService,
    private roomStateManager: RoomStateManagerService,
    private appSettingsService: AppSettingsService,
    private screenshotPreprocess: ScreenshotPreprocessService,
    private snapShotService: SnapShotService,
    private authService: AuthService,
    private proToolsUiManagerService: ProToolsUiManagerService,
    private drawings: DrawingsHolderService
  ) {
    this.proToolsActive = this.proToolsUiManagerService.proToolsMod;
    this.proToolsUiManagerService.proToolsModActive.subscribe(value => {
      this.proToolsActive = value;
    });
  }

  private _gatherAccessoriesData(): Array<AccessorySaveData> {
    let accessories = [];
    for (let accessory of this.stageService.getAccessories()) {
      const position = new Position(
        accessory.getWorldPosition(new THREE.Vector3()).x,
        accessory.getWorldPosition(new THREE.Vector3()).y,
        accessory.getWorldPosition(new THREE.Vector3()).z
      );
      const rot = new THREE.Euler().setFromQuaternion(accessory.getWorldQuaternion(), 'XYZ');
      const rotation = new Rotation(rot.x, rot.y, rot.z);
      accessories.push(
        new AccessorySaveData(
          accessory.userData.entity.id,
          accessory.userData.entity.description,
          position,
          rotation,
          accessory.parent.userData.customId
        )
      );
    }
    return accessories;
  }

  private _gatherTiersData(): Array<any> {
    let tiers = [];
    let floors = this.stageService.getCompoundFloor().children;
    for (let i = 0; i < floors.length; i++) {
      const floor = floors[i];
      let tier = {
        mainFloor: floor.userData.mainFloor,
        height: floor.getWorldPosition(new THREE.Vector3()).y,
        points: [],
        customId: floor.userData.customId,
        parentCustomId: floor.userData.parentCustomId
      };
      for (let i = 0; i < floor.userData.points.length; i++) {
        const point = floor.userData.points[i];
        let windowsDoorsData: any = [];
        let awningsData: any = [];
        let trellisData: any = [];
        if (point.superstructureType === 'wall') {
          let wallId = point.superstructureId;
          windowsDoorsData = this._gatherWindowsDoorsData(floor, wallId);
          awningsData = this.awningSaveDataService.gatherAwningsData(floor, wallId);
          trellisData = this._gatherTrellisData(floor, wallId);
        }
        if (point.superstructureType && floor.userData.points[i].parent) {
          tier.points.push({
            x: point.x,
            y: point.y,
            superstructureType: point.superstructureType,
            windowsDoors: windowsDoorsData,
            awnings: awningsData,
            trellis: trellisData,
            distance: point.distance,
            width: point.width,
            height: point.height,
            material: point.material,
            railStart: point.railStart,
            railStop: point.railStop,
            noRail: point.noRail,
            parent: true
          });
        } else if (point.superstructureType && floor.userData.points[i].childCustomId) {
          tier.points.push({
            x: point.x,
            y: point.y,
            superstructureType: point.superstructureType,
            windowsDoors: windowsDoorsData,
            awnings: awningsData,
            trellis: trellisData,
            distance: point.distance,
            width: point.width,
            height: point.height,
            material: point.material,
            railStart: point.railStart,
            railStop: point.railStop,
            noRail: point.noRail,
            childCustomId: floor.userData.points[i].childCustomId,
            tierId: floor.userData.points[i].tierId
          });
        } else {
          tier.points.push({
            x: point.x,
            y: point.y,
            superstructureType: point.superstructureType,
            windowsDoors: windowsDoorsData,
            awnings: awningsData,
            trellis: trellisData,
            distance: point.distance,
            width: point.width,
            height: point.height,
            material: point.material,
            railStart: point.railStart,
            railStop: point.railStop,
            noRail: point.noRail
          });
        }
      }
      tiers.push(tier);
    }
    return tiers;
  }

  private _gatherWindowsDoorsData(floor: any, wallId: number): Array<any> {
    let windowsDoors = this.stageService.getWindowsDoorsByFloorAndWallId(floor, wallId);
    let result = [];
    if (windowsDoors.length > 0) {
      windowsDoors.map(wd => {
        const rot = new THREE.Euler().setFromQuaternion(wd.getWorldQuaternion(), 'XYZ');
        result.push({
          id: wd.userData.entity.id,
          position: new Position(
            wd.getWorldPosition(new THREE.Vector3()).x,
            wd.getWorldPosition(new THREE.Vector3()).y,
            wd.getWorldPosition(new THREE.Vector3()).z
          ),
          rotation: new Rotation(rot.x, rot.y, rot.z),
          height: wd.userData.entity.height,
          length: wd.userData.entity.length
        });
      });
    }
    return result;
  }

  private _gatherTrellisData(floor: any, wallId: number): Array<any> {
    const walls = floor.children.filter(element => element.name === 'wall');
    const wall = walls.find(element => element.userData.uuid === wallId);
    const trellisObjects = wall ? wall.children.filter(obj => obj.name === 'Trellis') : [];
    return trellisObjects.map(trellis => {
      const rot = new THREE.Euler().setFromQuaternion(trellis.getWorldQuaternion(), 'XYZ');
      return {
        id: trellis.userData.entity.id,
        position: new Position(
          trellis.getWorldPosition(new THREE.Vector3()).x,
          trellis.getWorldPosition(new THREE.Vector3()).y,
          trellis.getWorldPosition(new THREE.Vector3()).z
        ),
        rotation: new Rotation(rot.x, rot.y, rot.z)
      };
    });
  }

  private _updateProjectImage(saveAsNewProject: boolean) {
    const updateImage = () => {
      if (this.appSettingsService.is2D == true && this.snapShotService.projectImg == '') {
        const screenshot = this.screenshotPreprocess.clipEmptySpace2D();
        this.snapShotService.setProjectImg(screenshot);
      }
    };

    if (
      !saveAsNewProject &&
      this.projectManagerService.project.projectImg &&
      this.projectManagerService.project.projectImg.slice(-4) == '.png'
    ) {
      this._toDeleteProjectImg = this.projectManagerService.project.projectImg;
      updateImage();
      return of(true);
    } else {
      updateImage();
      return of(true);
    }
  }

  private _showToast(type: string, title: string, body: string) {
    this.toasterService.popAsync(type, title, body);
  }

  private _getSaveErrorMessage(err: any, fallback: string): string {
    if (!err) {
      return fallback;
    }

    if (typeof err === 'string' && err.trim()) {
      return err;
    }

    if (err.status === 413) {
      return 'Project is too large to save. Try removing some snapshots.';
    }

    const serverMessage = this._extractServerMessage(err);
    if (serverMessage) {
      if (/unique|already exists/i.test(serverMessage)) {
        return 'Project name must be unique';
      }
      return serverMessage;
    }

    if (err.status === 500) {
      return 'Server error while saving project.';
    }

    if (err.status === 422) {
      return 'Project data is invalid. Check the project name and try again.';
    }

    return fallback;
  }

  private _extractServerMessage(err: any): string | null {
    const body = err && err.error;

    if (!body) {
      return null;
    }

    if (typeof body === 'string' && body.trim()) {
      return body;
    }

    if (body.message) {
      return body.message;
    }

    if (body.error && body.error.message) {
      return body.error.message;
    }

    if (Array.isArray(body)) {
      const messages = body
        .map(item => item.constraints && Object.values(item.constraints)[0])
        .filter(Boolean);

      if (messages.length) {
        return messages.join(' ');
      }
    }

    return null;
  }

  private _hasInlineSnapshots(snapshots: string[] = []): boolean {
    return this.snapShotService.hasInlineSnapshots(snapshots);
  }

  private _isCreate(saveAsNewProject: boolean): boolean {
    return saveAsNewProject || !this.projectManagerService.getCurrentProjectId();
  }

  private _shouldDeferSnapshots(
    isAdmin: boolean,
    isCreate: boolean,
    pendingSnapshots: string[]
  ): boolean {
    if (isAdmin || !pendingSnapshots.length) {
      return false;
    }

    if (isCreate) {
      return true;
    }

    return this._hasInlineSnapshots(pendingSnapshots);
  }

  private _uploadSnapshots(projectId: string, snapshots: string[]): Observable<string[]> {
    return this.snapShotService.persistSnapshotsForProject(projectId, snapshots);
  }

  private _resolveSaveRole(): string | null {
    return this.authService.resolveRoleFromToken();
  }

  private _assignSaveProjectFn(
    saveAsNewProject: boolean
  ): { saveProjectFn: () => Observable<any>; successMessage: string } | null {
    const projectId = this.projectManagerService.getCurrentProjectId();
    const shouldUpdateExisting = !saveAsNewProject && !!projectId;
    const role = this._resolveSaveRole();

    if (!role) {
      return null;
    }

    if (shouldUpdateExisting) {
      if (role === 'admin') {
        return {
          saveProjectFn: this.projectManagerService.updateTemplate.bind(this.projectManagerService),
          successMessage: 'Template updated'
        };
      }

      if (role === 'user' || role === 'salesManager') {
        return {
          saveProjectFn: this.projectManagerService.updateProject.bind(this.projectManagerService),
          successMessage: 'Project updated'
        };
      }

      return null;
    }

    if (role === 'admin') {
      return {
        saveProjectFn: this.projectManagerService.saveTemplate.bind(this.projectManagerService),
        successMessage: 'Template saved'
      };
    }

    if (role === 'user' || role === 'salesManager') {
      return {
        saveProjectFn: this.projectManagerService.saveProject.bind(this.projectManagerService),
        successMessage: 'Project saved'
      };
    }

    return null;
  }

  save(projectName: string, saveAsNewProject: boolean, showMessages = true) {
    this._toDeleteProjectImg = null;
    return this.projectManagerService.isSavedProject.pipe(
      take(1),
      concatMap(savedRecently =>
        this._updateProjectImage(saveAsNewProject).pipe(switchMap(res => of([res, savedRecently])))
      ),
      concatMap(([res, savedRecently]) => {
        const snapshots = this.snapShotService.getSnapshots();
        const projectImg = this.rendererService.getScreenShot();

        return this.snapShotService.compressSnapshotsForSave(snapshots).pipe(
          switchMap(compressedSnapshots =>
            this.snapShotService.compressImageForSave(projectImg).pipe(
              map(compressedProjectImg => ({
                savedRecently,
                compressedSnapshots,
                compressedProjectImg
              }))
            )
          )
        );
      }),
      concatMap(({ savedRecently, compressedSnapshots, compressedProjectImg }) => {
        this.roomStateManager.setProjectName(projectName);

        const pendingSnapshots = compressedSnapshots;
        const role = this._resolveSaveRole();
        const isAdmin = role === 'admin';
        const isCreate = this._isCreate(saveAsNewProject);
        const deferSnapshots = this._shouldDeferSnapshots(isAdmin, isCreate, pendingSnapshots);

        if (!isAdmin) {
          this.projectManagerService.setSnapshots(deferSnapshots ? [] : pendingSnapshots);
          if (!deferSnapshots) {
            this.snapShotService.setSnapshots(pendingSnapshots);
          }
        }

        if (this.proToolsActive) {
          const items = this.drawings.getEditableItems();
          this.projectManagerService.setProToolsItems(items);
          if (items.activeTB) {
            let info;
            info = this.drawings.getTitleBlockInfo();
            if (info.TopItems) {
              this.projectManagerService.setTitleBlockOptions(info);
            }
          }
        }
        this.projectManagerService.setDeckHeight(this.deckHeightService.getDeckHeight());
        this.projectManagerService.setProjectName(projectName);
        this.projectManagerService.setProjectImg(compressedProjectImg);
        this.projectManagerService.setTiers(this._gatherTiersData());
        this.projectManagerService.setUserId(this.authService.getUserId());
        this.projectManagerService.setAccessories(this._gatherAccessoriesData());
        this.projectManagerService.setDeckMaterial(
          this.setMaterialsService.getCurrentFloorTexture()
        );
        this.projectManagerService.setRailing(
          this.dataForDisplayingMenuItemsService.getCurrentParams()
        );
        this.projectManagerService.setSnapshotsProTools(
          this.snapShotService.getSnapshotsProTools()
        );

        const saveAction = this._assignSaveProjectFn(saveAsNewProject);
        const error = 'Cannot save project. Please try again.';

        if (!saveAction) {
          const roleError = 'Unable to determine user role. Please sign in again.';
          if (showMessages) {
            this._showToast('error', 'Save error', roleError);
          }
          return Observable.throw(roleError);
        }

        return of([
          saveAction.saveProjectFn,
          saveAction.successMessage,
          error,
          pendingSnapshots,
          deferSnapshots
        ]);
      }),
      concatMap(([saveProjectFn, successMessage, error, pendingSnapshots, deferSnapshots]) => {
        return saveProjectFn().pipe(
          switchMap((res: Project) => {
            if (!deferSnapshots || !pendingSnapshots.length) {
              return of(res);
            }

            const projectId = res.id || this.projectManagerService.getCurrentProjectId();
            return this._uploadSnapshots(projectId, pendingSnapshots).pipe(
              map(savedSnapshots => {
                this.snapShotService.setSnapshots(savedSnapshots);
                this.projectManagerService.setSnapshots(savedSnapshots);
                return res;
              })
            );
          }),
          switchMap((res: Project) => {
            if (showMessages) this._showToast('success', 'Success', successMessage);
            this.projectSaved$.next();
            return of(res);
          }),
          catchError(err => {
            const message = this._getSaveErrorMessage(err, error);
            if (showMessages) {
              this._showToast('error', 'Save error', message);
            }
            return Observable.throw(err);
          })
        );
      }),
      catchError(err => {
        return Observable.throw(err);
      }),
      concatMap(() => {
        if (this._toDeleteProjectImg) {
          return this.snapShotService.deleteSnapShot(this._toDeleteProjectImg).pipe(
            catchError(() => of(true))
          );
        }
        return of(true);
      })
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/services/project-save.service.ts